import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Check, ChevronDown, X, CircleCheck } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { usePipelineReviewExercises } from '@/lib/admin/pipeline-queries';
import type { ReviewExercise } from '@/lib/admin/pipeline-queries';
import { usePublishExercise, useRejectExercise } from '@/lib/admin/pipeline-mutations';
import { DIFFICULTY_LABEL } from '@/lib/admin/pipeline-labels';
import { MarkdownMath } from '@jeraisonne/ui';

export function TutorAdminPipelineReviewPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const { data: exercises, isLoading } = usePipelineReviewExercises(runId);

  const pending = (exercises ?? []).filter((e) => e.status === 'awaiting_review');

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/pipeline/$runId"
        params={{ runId }}
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Détail du run
      </Link>

      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Relire les exercices
      </h1>
      <p className="text-ink-500 mt-1 font-sans text-sm">
        Étape 12 — publie ou rejette chaque exercice. À la publication, une illustration (style
        manga) est générée puis l’exercice passe en ligne.
      </p>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-ink-100 h-44 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : pending.length === 0 ? (
        <div className="border-ink-200 mt-6 flex flex-col items-center rounded-2xl border border-dashed bg-white p-10 text-center">
          <CircleCheck className="h-8 w-8 text-emerald-500" />
          <p className="font-display text-ink-700 mt-3 text-sm font-bold">
            Aucun exercice en attente
          </p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Tous les exercices générés ont été traités.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {pending.map((ex) => (
            <ReviewCard key={ex.id} runId={runId} exercise={ex} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ runId, exercise }: { runId: string; exercise: ReviewExercise }) {
  const publish = usePublishExercise();
  const reject = useRejectExercise();
  const [showSteps, setShowSteps] = useState(false);
  const busy = publish.isPending || reject.isPending;

  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-display border-ink-200 text-ink-600 inline-flex h-5 items-center rounded-full border px-2 text-[10px] font-bold uppercase">
          {DIFFICULTY_LABEL[exercise.difficulty]}
        </span>
        {exercise.chapter?.title && (
          <span className="text-ink-500 font-sans text-[12px]">{exercise.chapter.title}</span>
        )}
        {exercise.estimated_minutes != null && (
          <span className="text-ink-400 font-mono text-[11px]">
            ~{exercise.estimated_minutes} min
          </span>
        )}
      </div>

      <h3 className="font-display text-ink-900 text-[16px] font-extrabold">{exercise.title}</h3>

      {exercise.context_md && (
        <div className="text-ink-600 mt-2 text-[13px]">
          <MarkdownMath>{exercise.context_md}</MarkdownMath>
        </div>
      )}
      <div className="text-ink-800 mt-2 text-[13px]">
        <MarkdownMath>{exercise.statement_md}</MarkdownMath>
      </div>

      <button
        type="button"
        onClick={() => setShowSteps((v) => !v)}
        className="text-primary-600 hover:text-primary-800 mt-3 flex min-h-11 items-center gap-1.5 font-sans text-[13px] font-semibold"
      >
        <ChevronDown className={cn('h-4 w-4 transition-transform', showSteps && 'rotate-180')} />
        {exercise.questions.length} question{exercise.questions.length !== 1 ? 's' : ''} — corrigé &
        distracteurs
      </button>

      {showSteps && (
        <div className="border-ink-100 mt-2 space-y-3 border-t pt-3">
          {exercise.questions.map((step) => (
            <div key={step.display_order} className="bg-ink-50 rounded-xl p-3">
              <p className="font-display text-ink-800 text-[12px] font-bold">
                Question {step.display_order}
              </p>
              <div className="text-ink-700 mt-1 text-[13px]">
                <MarkdownMath>{step.prompt_md}</MarkdownMath>
              </div>
              <ul className="mt-2 space-y-1">
                {step.choices.map((c) => (
                  <li
                    key={c.display_order}
                    className={cn(
                      'flex items-start gap-1.5 rounded-lg px-2 py-1 text-[12px]',
                      c.is_correct ? 'bg-emerald-50' : 'bg-white',
                    )}
                  >
                    {c.is_correct ? (
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <X className="text-ink-300 mt-0.5 h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-800">{c.label_md}</span>
                      {!c.is_correct && c.error_type && (
                        <span className="text-ink-400 font-mono"> · {c.error_type}</span>
                      )}
                      {c.feedback_md && (
                        <span className="text-ink-400 block italic">{c.feedback_md}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {step.explanation_md && (
                <div className="text-ink-500 mt-1.5 text-[12px] italic">
                  <MarkdownMath>{step.explanation_md}</MarkdownMath>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions — primaire (Publier) en haut du DOM, bas visuellement */}
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          variant="outline"
          onClick={() => reject.mutate({ runId, exerciseId: exercise.id })}
          disabled={busy}
          className="min-h-11 w-full sm:w-auto"
        >
          {reject.isPending ? 'Rejet…' : 'Rejeter'}
        </Button>
        <Button
          onClick={() => publish.mutate({ runId, exerciseId: exercise.id })}
          disabled={busy}
          className="min-h-11 w-full sm:flex-1"
        >
          {publish.isPending ? 'Génération de l’image…' : 'Publier l’exercice'}
        </Button>
      </div>
      {(publish.isError || reject.isError) && (
        <p className="text-accent-600 mt-2 font-sans text-[12px]">
          {(publish.error ?? reject.error)?.message}
        </p>
      )}
    </div>
  );
}
